import { Avatar, Box, CircularProgress, Icon, InputAdornment, Typography } from '@mui/material'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxInput from 'components/custom/AdxInput'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import React, { useEffect, useRef, useState } from 'react'
import LeaveCalendar from '../LeaveCalendar'
import { format } from 'date-fns'
import { useMutation, useQuery } from 'react-query'
import { getStaffList, generateIndividualReport } from 'services/api/modules/leave/leaveReport'
import defaultAvatar from 'assets/images/default_avatar.png'
import AdxButton from 'components/custom/AdxButton'
import { downloadResponseFile } from 'services/helper/fileDownload'

const IndividualReportDrawer = () => {
  const currentYear = new Date().getFullYear()
  const employeeRef = useRef()
  const containerRef = useRef()

  const [drawerState, setDrawerState] = useState({ right: false })
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedDate, setSelectedDate] = useState({ start: null, end: null })
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [searchDisplay, setSearchDisplay] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [staffList, setStaffList] = useState([])
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)

  const { refetch, isLoading, isFetching } = useQuery(
    ['allStaffData', searchQuery],
    async () => {
      if (drawerState.right) {
        const { data } = await getStaffList({
          page,
          ...(searchDisplay && { search: searchDisplay }),
        })
        return data
      }
    },
    {
      refetchOnWindowFocus: false,
      enabled: drawerState.right,
      onSuccess: (res) => {
        if (drawerState.right) {
          setStaffList((prevData) => [...prevData, ...res.data])
          !!res.next_page_url ? setHasMore(true) : setHasMore(false)
          setPage((currentPage) => currentPage + 1)
        }
      },
    }
  )

  const filenameFormatter = (inputString) => {
    const formattedString = inputString.replace(/\s/g, '_')
    return formattedString.toLowerCase()
  }

  const { mutate: exportLeaveReport } = useMutation(
    (data) => generateIndividualReport(selectedEmployee.id, data),
    {
      onSuccess: (res) => {
        downloadResponseFile(res, {
          fileName: `${filenameFormatter(selectedEmployee.name)}_leave_report.xlsx`,
          contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          successMessage: 'Individual leave report downloaded.',
        })
      },
      onError: (err) => console.error(err),
    }
  )

  const dateFormatter = (date) => {
    if (date) {
      return format(date, 'dd/MM/yyyy')
    }
  }

  const getYearArray = () => {
    const currentYear = new Date().getFullYear()
    const yearArray = []

    for (let i = currentYear; i >= 2021; i--) {
      yearArray.push(i)
    }

    return yearArray
  }

  const handleDrawer = (anchor, open) => () => {
    setDrawerState({ ...drawerState, [anchor]: open })
  }

  const handleReset = () => {
    setSearchDisplay('')
    setSearchQuery('')
    setPage(1)
    setStaffList([])
  }

  const handleFocus = () => {
    if (!isFocused) {
      refetch()
    }
    setIsFocused(true)
  }

  const handleBlur = (event) => {
    if (employeeRef.current && !employeeRef.current.contains(event.target)) {
      setIsFocused(false)
      handleReset()
    }
  }

  const handleSearch = (e) => {
    setStaffList([])
    setPage(1)
    setSearchDisplay(e.target.value)
    setTimeout(() => {
      setSearchQuery(e.target.value)
    }, 1000)
  }

  const handleSelectEmployee = (e) => {
    setSelectedEmployee(e)
    setIsFocused(false)
    handleReset()
  }

  const handleExport = () => {
    const body = {
      start_date: format(selectedDate.start, 'yyyy-MM-dd'),
      end_date: format(selectedDate.end, 'yyyy-MM-dd'),
    }

    exportLeaveReport(body)
  }

  const handleScroll = () => {
    const container = containerRef.current

    if (container) {
      const isAtBottom = container.scrollHeight - container.scrollTop === container.clientHeight

      if (isAtBottom && hasMore) {
        console.log('Scrolled to bottom')
        refetch()
      }
    }
  }

  useEffect(() => {
    if (searchQuery === '') {
      refetch()
    }
  }, [searchQuery])

  useEffect(() => {
    document.addEventListener('click', handleBlur)

    return () => {
      document.removeEventListener('click', handleBlur)
    }
  }, [])

  return (
    <>
      <AdxButton
        variant={'contained'}
        sx={{ width: '145px', marginLeft: '16px' }}
        onClick={handleDrawer('right', true)}
      >
        Generate report
      </AdxButton>
      <AdxDrawerComponent
        state={drawerState}
        anchor="right"
        onClose={handleDrawer}
        title={'Generate individual report'}
        headerprops
        footerprops={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: 1,
            }}
          >
            <Box>
              {selectedDate.start && (
                <>
                  <Typography marginBottom="4px" fontWeight="bold">
                    {`${dateFormatter(selectedDate.start)} ${
                      selectedDate.end ? `- ${dateFormatter(selectedDate.end)}` : ''
                    }`}
                  </Typography>
                  <Typography variant="subtitle2" color="light-gray-text.main">
                    Date selected
                  </Typography>
                </>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <AdxButton
                variant={'outlined'}
                sx={{ width: '100px' }}
                onClick={handleDrawer('right', false)}
              >
                Cancel
              </AdxButton>
              <AdxButton
                variant={'contained'}
                sx={{ width: '200px' }}
                disabled={selectedDate.end === null}
                onClick={handleExport}
              >
                Export as xlsx
              </AdxButton>
            </Box>
          </Box>
        }
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            pb: 10,
          }}
        >
          <Box borderBottom={1} borderColor="gray-line.main" sx={{ pb: 2 }}>
            <Typography>Employee</Typography>
            <Box
              ref={employeeRef}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                marginTop: '8px',
              }}
            >
              <Box
                onClick={handleFocus}
                sx={{
                  border: '1px solid',
                  borderColor: isFocused ? 'alternate.main' : 'light-gray-text.main',
                  borderRadius: '4.5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingX: '14px',
                  height: '60px',
                  cursor: 'pointer',
                  marginBottom: '3px',
                }}
              >
                {selectedEmployee !== null ? (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <Avatar
                        sx={{ height: '40px', width: '40px' }}
                        src={selectedEmployee.profile_image || defaultAvatar}
                      />
                    </Box>
                    <Box>
                      <Typography>{selectedEmployee.name}</Typography>
                      <Typography variant="subtitle2" color="light-gray-text.main">
                        {selectedEmployee.position}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography color="light-gray-text.main">Select</Typography>
                )}
                <Icon
                  sx={{
                    color: isFocused ? 'alternate.main' : 'light-gray-text.main',
                    rotate: isFocused ? '-90deg' : '90deg',
                  }}
                >
                  chevron_right
                </Icon>
              </Box>
              {isFocused && (
                <Box
                  sx={{
                    backgroundColor: 'white.main',
                    width: '100%',
                    padding: '0px 9px 7px',
                    border: '1px solid #E5E5E5',
                    boxShadow: '0px 0px 10px 4px rgba(0, 0, 0, 0.05)',
                    borderRadius: '7px',
                    position: 'absolute',
                    top: '100%',
                    zIndex: 1,
                  }}
                >
                  <AdxInput
                    placeholder="Search employee name"
                    value={searchDisplay}
                    onChange={handleSearch}
                    sx={{
                      backgroundColor: 'white.main',
                      paddingTop: '10px',
                      paddingBottom: '5px',
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Icon>search</Icon>
                      </InputAdornment>
                    }
                  />
                  <Box
                    ref={containerRef}
                    onScroll={handleScroll}
                    sx={{
                      maxHeight: '40vh',
                      overflowY: 'scroll',
                    }}
                    className="scrollbar"
                  >
                    {staffList.map((i, index) => (
                      <Box
                        key={index}
                        onClick={() => handleSelectEmployee(i)}
                        sx={{
                          padding: '16px 14px',
                          borderRadius: '7.5px',
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center',
                          '&:hover': {
                            backgroundColor: 'alternate.light',
                            cursor: 'pointer',
                          },
                        }}
                      >
                        <Box>
                          <Avatar
                            sx={{ height: '40px', width: '40px' }}
                            src={i.profile_image || defaultAvatar}
                          />
                        </Box>
                        <Box>
                          <Typography>{i.name}</Typography>
                          <Typography variant="subtitle2" color="light-gray-text.main">
                            {i.position}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                    {(isLoading || isFetching) && (
                      <Box width="100%" display="flex" justifyContent="center" paddingY={3}>
                        <CircularProgress color="alternate" size={30} />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
            <AdxDropdown
              sx={{ width: 1 }}
              defaultValue={currentYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {getYearArray().map((i, index) => (
                <AdxMenuItem key={index} selectedvalue={selectedYear} value={i}>
                  {i}
                </AdxMenuItem>
              ))}
            </AdxDropdown>
            <LeaveCalendar
              selectedYear={selectedYear}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />
          </Box>
        </Box>
      </AdxDrawerComponent>
    </>
  )
}

export default IndividualReportDrawer
